import { ChangeEvent, ReactNode, useContext, useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { useSearchParams } from 'libs/router'
import { format } from 'libs/dates'
import {
	DatePicker,
	FilterField,
	FiltersPanel,
	FiltersToggle,
	SearchInput,
	Select
} from 'shared/components'
import { useOptions } from 'shared/hooks/useOptions'
import { AuthContext } from 'store'
import { apiRoutes } from 'config/apiRoutes'
import { parseDate } from 'shared/components/DatePickerRange/utils/utils'
import { useFilters } from '../../../../hooks'
import { boothOptionLabel } from '../../../../hooks/useBookingOptions'
import type { AreaItem } from 'features/areas'
import { cn } from 'utils/style'

const FILTER_PARAMS = ['is_active', 'status', 'area_id', 'start_date', '_end_date']

type FiltersSectionProps = {
	actions?: ReactNode
	handleSearch: (data: ChangeEvent<HTMLInputElement>) => void
	handleFilter: ({ id, value }: { id: string; value: string | null }) => void
}

export const FiltersSection = ({ actions, handleSearch, handleFilter }: FiltersSectionProps) => {
	const { t } = useTranslation()
	const [searchParams, setSearchParams] = useSearchParams()
	const { auth } = useContext(AuthContext)
	const filters = useFilters()
	const [isOpen, setIsOpen] = useState(FILTER_PARAMS.some((param) => searchParams.has(param)))
	const [resetKey, setResetKey] = useState(0)
	const [start, setStart] = useState<Date | undefined>(parseDate(searchParams.get('start_date')))
	const [end, setEnd] = useState<Date | undefined>(parseDate(searchParams.get('_end_date')))

	const booths = useOptions<AreaItem>({
		name: 'fetchAreas',
		endpoint: apiRoutes.areas(),
		enabled: !!auth.accessToken,
		getLabel: boothOptionLabel
	})

	const handleDateFilter = (id: 'start_date' | '_end_date', date: Date | null) => {
		if (id === 'start_date') setStart(date ?? undefined)
		else setEnd(date ?? undefined)

		handleFilter({ id, value: date ? format(date, 'yyyy-MM-dd') : null })
	}

	const clearFilters = () => {
		const params = new URLSearchParams(searchParams)
		FILTER_PARAMS.forEach((param) => params.delete(param))
		params.delete('cursor')
		setSearchParams(params)
		setStart(undefined)
		setEnd(undefined)
		setResetKey((key) => key + 1)
	}

	return (
		<div className="border-b border-border p-4">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
				{actions}
				<SearchInput
					variant="filled"
					className="min-w-0 flex-1"
					label={t('Search bookings')}
					placeholder={t('Search bookings...')}
					onChange={handleSearch}
				/>
				<FiltersToggle
					className="lg:hidden"
					isOpen={isOpen}
					onClick={() => setIsOpen((open) => !open)}
				/>
			</div>
			<div className={cn('lg:block', !isOpen && 'hidden')}>
				<FiltersPanel key={resetKey} title={t('Filter bookings')} onClear={clearFilters}>
					{filters.map((filter) => (
						<FilterField key={filter.id} label={filter.label}>
							<Select
								id={filter.id}
								variant="outline"
								placeholder={filter.placeholder}
								options={filter.options}
								onChange={(data) => handleFilter({ id: filter.id, value: data?.value || null })}
							/>
						</FilterField>
					))}
					<FilterField label={t('Area')}>
						<Select
							id="area_id"
							variant="outline"
							placeholder={t('All')}
							isLoading={booths.isLoading}
							options={booths.options}
							handleInfiniteScroll={booths.fetchNextPage}
							onChange={(data) => handleFilter({ id: 'area_id', value: data?.value || null })}
						/>
					</FilterField>
					<FilterField label={t('Start')}>
						<DatePicker
							label=""
							variant="outline"
							selected={start}
							onChange={(date) => handleDateFilter('start_date', date)}
						/>
					</FilterField>
					<FilterField label={t('End')}>
						<DatePicker
							label=""
							variant="outline"
							selected={end}
							onChange={(date) => handleDateFilter('_end_date', date)}
						/>
					</FilterField>
				</FiltersPanel>
			</div>
		</div>
	)
}
